import { useState, useMemo, useId, useEffect, useCallback } from 'react';
import {
  X,
  Plus,
  ChevronDown,
  Loader2,
  Brush,
  AlertTriangle,
  CheckCircle2,
  Home,
  Search,
  ShieldOff,
  SprayCan,
  Clock,
  User,
  UserPlus,
  Users,
  Mail,
  Phone as PhoneIcon,
} from 'lucide-react';
import { HousekeepingStatus, UserRole } from '@pms/shared';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { useAuth } from '@/hooks/useAuth';
import { useUnits } from '@/hooks/api/useProperties';
import { useSelectedPropertyId, usePropertyContext } from '@/contexts/PropertyContext';
import {
  useTasks,
  useCreateTask,
  useUpdateHousekeepingStatus,
  useHousekeepingStaff,
  useBatchAssignHousekeeping,
} from '@/hooks/api/useOperations';
import { Link } from 'react-router-dom';
import { SlideOver } from '@/components/ui/SlideOver';
import { Button } from '@/components/ui/Button';
import { useInviteUser } from '@/hooks/api/useUsers';
import { useHousekeepingServiceTypes } from '@/hooks/api/useHousekeepingServiceTypes';
import type { InviteUserResponse } from '@/services/api/users.api';
import type { StaffMember, CreateTaskPayload } from '@/services/api/operations.api';

// ── Constants ────────────────────────────────────────────────────────────────

const ALLOWED_ROLES: UserRole[] = [UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.OPERATIONS];

const HK_CONFIG: Record<
  HousekeepingStatus,
  { label: string; bg: string; border: string; badge: string; dot: string }
> = {
  [HousekeepingStatus.CLEAN]: {
    label: 'Clean',
    bg: 'bg-white',
    border: 'border-l-green-500',
    badge: 'bg-green-100 text-green-700',
    dot: 'bg-green-500',
  },
  [HousekeepingStatus.INSPECTED]: {
    label: 'Inspected',
    bg: 'bg-white',
    border: 'border-l-blue-500',
    badge: 'bg-blue-100 text-blue-700',
    dot: 'bg-blue-500',
  },
  [HousekeepingStatus.DIRTY]: {
    label: 'Dirty',
    bg: 'bg-red-50',
    border: 'border-l-red-500',
    badge: 'bg-red-100 text-red-700',
    dot: 'bg-red-500',
  },
  [HousekeepingStatus.OCCUPIED]: {
    label: 'Occupied',
    bg: 'bg-yellow-50',
    border: 'border-l-yellow-500',
    badge: 'bg-yellow-100 text-yellow-700',
    dot: 'bg-yellow-500',
  },
  [HousekeepingStatus.MAINTENANCE]: {
    label: 'Maintenance',
    bg: 'bg-orange-50',
    border: 'border-l-orange-500',
    badge: 'bg-orange-100 text-orange-700',
    dot: 'bg-orange-500',
  },
  [HousekeepingStatus.OUT_OF_ORDER]: {
    label: 'Out of Order',
    bg: 'bg-gray-50',
    border: 'border-l-gray-500',
    badge: 'bg-gray-100 text-gray-700',
    dot: 'bg-gray-500',
  },
};

type StatusFilterValue = 'all' | HousekeepingStatus;

const STATUS_FILTER_OPTIONS: { value: StatusFilterValue; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  ...Object.values(HousekeepingStatus).map((s) => ({
    value: s as StatusFilterValue,
    label: HK_CONFIG[s].label,
  })),
];

type TaskType = 'housekeeping' | 'maintenance' | 'inspection' | 'deep_clean' | 'turnover' | 'laundry' | 'minibar' | 'amenity_restock' | 'pest_control' | 'security' | 'guest_request' | 'custom' | 'other';
type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

const TASK_TYPE_LABELS: Record<string, string> = {
  housekeeping: 'Housekeeping',
  maintenance: 'Maintenance',
  inspection: 'Inspection',
  deep_clean: 'Deep Clean',
  turnover: 'Turnover',
  laundry: 'Laundry',
  minibar: 'Minibar',
  amenity_restock: 'Amenity Restock',
  pest_control: 'Pest Control',
  security: 'Security',
  guest_request: 'Guest Request',
  other: 'Other',
  custom: 'Custom...',
};

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

// ── Types ────────────────────────────────────────────────────────────────────

interface EnrichedUnit {
  id: string;
  name: string;
  type: string;
  housekeepingStatus: HousekeepingStatus;
  propertyName: string;
  propertyId: string;
  assignedTo?: string | undefined;
}

// ── Access Denied ────────────────────────────────────────────────────────────

function AccessDeniedCard() {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-red-200 bg-white py-24 px-6 text-center gap-4">
      <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-red-50">
        <ShieldOff size={32} className="text-red-400" aria-hidden="true" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-gray-700">Access Denied</h2>
        <p className="mt-1 text-sm text-gray-400 max-w-sm">
          Housekeeping is restricted to Admins and Operations staff.
        </p>
      </div>
    </div>
  );
}

// ── Summary Stats ────────────────────────────────────────────────────────────

function SummaryStats({ units }: { units: EnrichedUnit[] }) {
  const dirty = units.filter((u) => u.housekeepingStatus === HousekeepingStatus.DIRTY).length;
  const occupied = units.filter((u) => u.housekeepingStatus === HousekeepingStatus.OCCUPIED).length;
  const clean = units.filter(
    (u) =>
      u.housekeepingStatus === HousekeepingStatus.CLEAN ||
      u.housekeepingStatus === HousekeepingStatus.INSPECTED,
  ).length;
  const outOfOrder = units.filter(
    (u) =>
      u.housekeepingStatus === HousekeepingStatus.OUT_OF_ORDER ||
      u.housekeepingStatus === HousekeepingStatus.MAINTENANCE,
  ).length;

  const stats = [
    {
      label: 'To Clean',
      value: dirty,
      icon: <Brush size={18} className="text-red-500" aria-hidden="true" />,
      bg: 'bg-red-50',
    },
    {
      label: 'In Progress',
      value: occupied,
      icon: <Clock size={18} className="text-yellow-500" aria-hidden="true" />,
      bg: 'bg-yellow-50',
    },
    {
      label: 'Clean',
      value: clean,
      icon: <CheckCircle2 size={18} className="text-green-500" aria-hidden="true" />,
      bg: 'bg-green-50',
    },
    {
      label: 'Out of Order',
      value: outOfOrder,
      icon: <AlertTriangle size={18} className="text-gray-500" aria-hidden="true" />,
      bg: 'bg-gray-50',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {stats.map((s) => (
        <div
          key={s.label}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-3"
        >
          <div
            className={cn(
              'flex items-center justify-center w-9 h-9 rounded-lg shrink-0',
              s.bg,
            )}
          >
            {s.icon}
          </div>
          <div>
            <p className="text-xl font-bold text-gray-900 leading-none">{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Create Task SlideOver Form ──────────────────────────────────────────────

interface CreateTaskFormProps {
  units: EnrichedUnit[];
  staff: StaffMember[];
  propertyId: string;
  propertyName: string;
  defaultUnitId?: string | undefined;
  onClose: () => void;
}

function CreateTaskForm({ units, staff, propertyId, propertyName, defaultUnitId, onClose }: CreateTaskFormProps) {
  const unitSelectId = useId();
  const typeSelectId = useId();
  const serviceTypeSelectId = useId();
  const titleInputId = useId();
  const prioritySelectId = useId();
  const assigneeSelectId = useId();
  const dueDateInputId = useId();
  const descriptionId = useId();

  const today = new Date().toISOString().split('T')[0];

  const [unitId, setUnitId] = useState(defaultUnitId ?? (units[0]?.id ?? ''));
  const [taskType, setTaskType] = useState<TaskType>('housekeeping');
  const [customType, setCustomType] = useState('');
  const [serviceTypeId, setServiceTypeId] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueDate, setDueDate] = useState(today);
  const [description, setDescription] = useState('');

  // Optional: populate a Service Type picker from active HK service types.
  const { data: serviceTypesData } = useHousekeepingServiceTypes(
    { propertyId, isActive: true },
    !!propertyId,
  );
  const serviceTypes = serviceTypesData?.data ?? [];

  const selectedUnit = units.find((u) => u.id === unitId);
  const autoTitle = useMemo(() => {
    const typeLabel = taskType === 'housekeeping' ? 'Clean' : taskType === 'inspection' ? 'Inspect' : 'Maintain';
    return `${typeLabel} — ${selectedUnit?.name ?? 'Unit'}`;
  }, [taskType, selectedUnit]);

  const [title, setTitle] = useState(autoTitle);

  // Update title when auto-generated values change, but only if title matches previous auto
  const prevAutoRef = useMemo(() => autoTitle, [autoTitle]);
  useEffect(() => {
    setTitle(prevAutoRef);
  }, [prevAutoRef]);

  const createTaskMutation = useCreateTask();

  function clearError() {
    if (createTaskMutation.isError) createTaskMutation.reset();
  }

  const resolvedTaskType = taskType === 'custom' ? (customType.trim() || 'other') : taskType;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!unitId || !title.trim()) return;

    const payload: CreateTaskPayload = {
      unitId,
      taskType: resolvedTaskType,
      title: title.trim(),
      description: description.trim() || undefined,
      assignedTo: assignedTo || null,
      priority,
      dueAt: dueDate || null,
      ...(serviceTypeId ? { serviceTypeId } : {}),
    };

    createTaskMutation.mutate(payload, {
      onSuccess: () => onClose(),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
        <p className="text-xs text-gray-500">Property</p>
        <p className="text-sm font-medium text-gray-900">{propertyName}</p>
      </div>

      <div>
        <label htmlFor={unitSelectId} className="block text-sm font-medium text-gray-700 mb-1">
          Unit <span aria-label="required" className="text-red-500">*</span>
        </label>
        <div className="relative">
          <select
            id={unitSelectId}
            required
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
            className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="" disabled>Select a unit</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>{u.name} ({u.type})</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
        </div>
      </div>

      <div>
        <label htmlFor={typeSelectId} className="block text-sm font-medium text-gray-700 mb-1">
          Task Type
        </label>
        <div className="relative">
          <select
            id={typeSelectId}
            value={taskType}
            onChange={(e) => { setTaskType(e.target.value as TaskType); clearError(); }}
            className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {Object.entries(TASK_TYPE_LABELS).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
        </div>
        {taskType === 'custom' && (
          <input
            type="text"
            placeholder="Enter custom task type"
            value={customType}
            onChange={(e) => { setCustomType(e.target.value); clearError(); }}
            className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        )}
      </div>

      {serviceTypes.length > 0 && (
        <div>
          <label htmlFor={serviceTypeSelectId} className="block text-sm font-medium text-gray-700 mb-1">
            Service Type
          </label>
          <div className="relative">
            <select
              id={serviceTypeSelectId}
              value={serviceTypeId}
              onChange={(e) => { setServiceTypeId(e.target.value); clearError(); }}
              className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">None (manual)</option>
              {serviceTypes.map((st) => (
                <option key={st.id} value={st.id}>
                  {st.name}{st.estimatedMinutes != null ? ` (${st.estimatedMinutes} min)` : ''}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
          </div>
        </div>
      )}

      <div>
        <label htmlFor={titleInputId} className="block text-sm font-medium text-gray-700 mb-1">
          Title <span aria-label="required" className="text-red-500">*</span>
        </label>
        <input
          id={titleInputId}
          type="text"
          required
          value={title}
          onChange={(e) => { setTitle(e.target.value); clearError(); }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div>
        <label htmlFor={prioritySelectId} className="block text-sm font-medium text-gray-700 mb-1">
          Priority
        </label>
        <div className="relative">
          <select
            id={prioritySelectId}
            value={priority}
            onChange={(e) => setPriority(e.target.value as TaskPriority)}
            className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {PRIORITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
        </div>
      </div>

      <div>
        <label htmlFor={assigneeSelectId} className="block text-sm font-medium text-gray-700 mb-1">
          Assign To
        </label>
        <div className="relative">
          <select
            id={assigneeSelectId}
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Unassigned</option>
            {staff.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
        </div>
      </div>

      <div>
        <label htmlFor={dueDateInputId} className="block text-sm font-medium text-gray-700 mb-1">
          Due Date
        </label>
        <input
          id={dueDateInputId}
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div>
        <label htmlFor={descriptionId} className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          id={descriptionId}
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional notes..."
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm resize-y placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {createTaskMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(createTaskMutation.error)}
        </p>
      )}

      <Button type="submit" fullWidth isLoading={createTaskMutation.isPending}>
        <Plus size={16} aria-hidden="true" />
        Create Task
      </Button>
    </form>
  );
}

// ── Unit Card ────────────────────────────────────────────────────────────────

interface UnitCardProps {
  unit: EnrichedUnit;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onClick: () => void;
  onQuickClean: () => void;
}

function UnitCard({ unit, isSelected, onToggleSelect, onClick, onQuickClean }: UnitCardProps) {
  const cfg = HK_CONFIG[unit.housekeepingStatus] ?? HK_CONFIG[HousekeepingStatus.CLEAN];
  const isDirty = unit.housekeepingStatus === HousekeepingStatus.DIRTY;

  return (
    <div
      className={cn(
        'rounded-xl border border-gray-200 border-l-4 p-3 transition-shadow hover:shadow-md',
        cfg.bg,
        cfg.border,
        isSelected && 'ring-2 ring-primary-500 ring-offset-1',
      )}
    >
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(unit.id)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          aria-label={`Select unit ${unit.name}`}
        />
        <button
          type="button"
          onClick={onClick}
          className="flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
          aria-label={`Unit ${unit.name}, status: ${cfg.label}. Click to update status.`}
        >
          <div className="flex items-start justify-between gap-1 mb-1">
            <span className="font-semibold text-gray-900 text-sm truncate">{unit.name}</span>
            <span
              className={cn(
                'inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-full shrink-0',
                cfg.badge,
              )}
            >
              <span
                className={cn('w-1.5 h-1.5 rounded-full shrink-0', cfg.dot)}
                aria-hidden="true"
              />
              {cfg.label}
            </span>
          </div>
          <p className="text-xs text-gray-500 truncate">{unit.type}</p>
          <p className="text-xs text-gray-400 mt-1 truncate flex items-center gap-1">
            <User size={10} aria-hidden="true" />
            {unit.assignedTo || 'Unassigned'}
          </p>
        </button>
      </div>
      {isDirty && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onQuickClean();
          }}
          className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          aria-label={`Create cleaning task for ${unit.name}`}
        >
          <SprayCan size={12} aria-hidden="true" />
          Create Cleaning Task
        </button>
      )}
    </div>
  );
}

// ── Status Change Modal ──────────────────────────────────────────────────────

function StatusChangeModal({
  unit,
  onClose,
}: {
  unit: EnrichedUnit | null;
  onClose: () => void;
}) {
  const [status, setStatus] = useState<HousekeepingStatus>(HousekeepingStatus.CLEAN);
  const updateMutation = useUpdateHousekeepingStatus();
  const titleId = useId();

  useEffect(() => {
    if (unit) setStatus(unit.housekeepingStatus);
  }, [unit]);

  if (!unit) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!unit) return;
    updateMutation.mutate({ unitId: unit.id, status }, { onSuccess: onClose });
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" aria-hidden="true" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 id={titleId} className="text-base font-semibold text-gray-900">
              Update Unit Status
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">{unit.name}</p>
              <p className="text-xs text-gray-400">{unit.propertyName}</p>
              <p className="text-xs text-gray-500 mt-1">
                Current assignee: {unit.assignedTo || 'Unassigned'}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">
                Housekeeping Status
              </label>
              <div className="grid grid-cols-2 gap-2">
                {(
                  Object.entries(HK_CONFIG) as [
                    HousekeepingStatus,
                    (typeof HK_CONFIG)[HousekeepingStatus],
                  ][]
                ).map(([key, cfg]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setStatus(key)}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
                      status === key
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50',
                    )}
                  >
                    <span
                      className={cn('w-2.5 h-2.5 rounded-full shrink-0', cfg.dot)}
                      aria-hidden="true"
                    />
                    {cfg.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60"
              >
                {updateMutation.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : null}
                Update Status
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

// ── Staff Card ───────────────────────────────────────────────────────────────

interface StaffCardProps {
  staff: StaffMember;
  isSelected: boolean;
  onSelect: () => void;
  assignedCount: number;
}

function StaffCard({ staff, isSelected, onSelect, assignedCount }: StaffCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full text-left rounded-lg border p-3 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        isSelected
          ? 'border-primary-500 bg-primary-50'
          : 'border-gray-200 bg-white hover:bg-gray-50',
      )}
      aria-pressed={isSelected}
      aria-label={`Select ${staff.name}, ${staff.role}, ${assignedCount} tasks assigned`}
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-500 shrink-0">
          <User size={16} aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{staff.name}</p>
          <p className="text-xs text-gray-500">{staff.role}</p>
        </div>
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs font-bold text-gray-600 shrink-0">
          {assignedCount}
        </span>
      </div>
    </button>
  );
}

// ── Assignment Panel ─────────────────────────────────────────────────────────

interface AssignmentPanelProps {
  selectedUnitIds: Set<string>;
  tasks: Array<Record<string, unknown>>;
  propertyId: string;
  onAssignComplete: () => void;
}

function AddStaffForm({ propertyId }: { propertyId: string }) {
  const nameInputId = useId();
  const emailInputId = useId();
  const phoneInputId = useId();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const inviteMutation = useInviteUser();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSuccessMessage(null);

    const trimmedName = name.trim();
    const parts = trimmedName.split(/\s+/);
    const firstName = parts[0] ?? '';
    const lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';

    const payload = {
      email: email.trim(),
      role: UserRole.OPERATIONS as const,
      firstName,
      propertyIds: [propertyId],
      ...(lastName ? { lastName } : {}),
    };

    inviteMutation.mutate(
      payload,
      {
        onSuccess: (data: InviteUserResponse) => {
          setSuccessMessage(
            `Staff added. Temporary password: ${data.temporaryPassword}`,
          );
          setName('');
          setEmail('');
          setPhone('');
        },
      },
    );
  }

  function clearError() {
    if (inviteMutation.isError) inviteMutation.reset();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label htmlFor={nameInputId} className="block text-xs font-medium text-gray-600 mb-1">
          Name <span aria-label="required" className="text-red-500">*</span>
        </label>
        <div className="relative">
          <User size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
          <input
            id={nameInputId}
            type="text"
            required
            value={name}
            onChange={(e) => { setName(e.target.value); clearError(); }}
            placeholder="First Last"
            className="w-full rounded-lg border border-gray-300 bg-white pl-8 pr-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div>
        <label htmlFor={emailInputId} className="block text-xs font-medium text-gray-600 mb-1">
          Email <span aria-label="required" className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Mail size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
          <input
            id={emailInputId}
            type="email"
            required
            value={email}
            onChange={(e) => { setEmail(e.target.value); clearError(); }}
            placeholder="staff@example.com"
            className="w-full rounded-lg border border-gray-300 bg-white pl-8 pr-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div>
        <label htmlFor={phoneInputId} className="block text-xs font-medium text-gray-600 mb-1">
          Phone
        </label>
        <div className="relative">
          <PhoneIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
          <input
            id={phoneInputId}
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Optional"
            className="w-full rounded-lg border border-gray-300 bg-white pl-8 pr-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {inviteMutation.isError && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(inviteMutation.error)}
        </p>
      )}

      {successMessage && (
        <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2" role="status">
          {successMessage}
        </p>
      )}

      <button
        type="submit"
        disabled={inviteMutation.isPending}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
      >
        {inviteMutation.isPending ? (
          <Loader2 size={14} className="animate-spin" aria-hidden="true" />
        ) : (
          <UserPlus size={14} aria-hidden="true" />
        )}
        Add to Team
      </button>
    </form>
  );
}

function AssignmentPanel({ selectedUnitIds, tasks, propertyId, onAssignComplete }: AssignmentPanelProps) {
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const { data: staffData, isLoading: staffLoading } = useHousekeepingStaff(propertyId || undefined);
  const batchAssign = useBatchAssignHousekeeping();

  const staff: StaffMember[] = useMemo(() => {
    if (!staffData) return [];
    return Array.isArray(staffData) ? staffData : [];
  }, [staffData]);

  // Count tasks assigned per staff member
  const assignedCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of tasks) {
      const assignee = t.assignedTo as string;
      if (assignee) {
        counts[assignee] = (counts[assignee] || 0) + 1;
      }
    }
    return counts;
  }, [tasks]);

  // Get housekeeping task IDs for selected units
  const taskIdsForSelectedUnits = useMemo(() => {
    if (selectedUnitIds.size === 0) return [];
    return tasks
      .filter(
        (t) =>
          selectedUnitIds.has(t.unitId as string) &&
          // Include all housekeeping task sub-types (turnover, deep_clean, etc.)
          // not just the literal 'housekeeping' value — 'maintenance' tasks are excluded
          (t.taskType as string) !== 'maintenance' &&
          t.status !== 'completed',
      )
      .map((t) => t.id as string);
  }, [tasks, selectedUnitIds]);

  function handleAssign() {
    if (!selectedStaffId || taskIdsForSelectedUnits.length === 0) return;
    batchAssign.mutate(
      { taskIds: taskIdsForSelectedUnits, assigneeId: selectedStaffId },
      {
        onSuccess: () => {
          setSelectedStaffId(null);
          onAssignComplete();
        },
      },
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-gray-400" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-gray-900">
            Staff{!staffLoading && staff.length > 0 ? ` (${staff.length})` : ''}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => setShowAddStaff((prev) => !prev)}
          className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
          aria-expanded={showAddStaff}
          aria-controls="add-staff-form"
        >
          {showAddStaff ? (
            <>
              <X size={12} aria-hidden="true" />
              Cancel
            </>
          ) : (
            <>
              <Plus size={12} aria-hidden="true" />
              Add
            </>
          )}
        </button>
      </div>

      <div className="p-4 space-y-3">
        {selectedUnitIds.size > 0 && (
          <p className="text-xs text-primary-600 font-medium">
            {selectedUnitIds.size} unit{selectedUnitIds.size !== 1 ? 's' : ''} selected
          </p>
        )}

        {staffLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={20} className="animate-spin text-primary-600" aria-hidden="true" />
            <span className="ml-2 text-sm text-gray-500">Loading staff...</span>
          </div>
        ) : staff.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 mb-2">No staff assigned to this property</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto" role="listbox" aria-label="Housekeeping staff">
            {staff.map((member) => (
              <StaffCard
                key={member.id}
                staff={member}
                isSelected={selectedStaffId === member.id}
                onSelect={() =>
                  setSelectedStaffId((prev) => (prev === member.id ? null : member.id))
                }
                assignedCount={assignedCounts[member.id] ?? 0}
              />
            ))}
          </div>
        )}

        {/* Add Staff Member inline form */}
        {showAddStaff && (
          <div id="add-staff-form" className="border-t border-gray-100 pt-3">
            <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
              <UserPlus size={12} aria-hidden="true" />
              Add Staff Member
            </p>
            <AddStaffForm propertyId={propertyId} />
          </div>
        )}

        <button
          type="button"
          onClick={handleAssign}
          disabled={
            !selectedStaffId ||
            selectedUnitIds.size === 0 ||
            taskIdsForSelectedUnits.length === 0 ||
            batchAssign.isPending
          }
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          {batchAssign.isPending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : null}
          Assign Selected
        </button>

        {selectedUnitIds.size > 0 && taskIdsForSelectedUnits.length === 0 && (
          <p className="text-xs text-gray-400 text-center">
            No pending housekeeping tasks for the selected units.
          </p>
        )}

        <div className="text-center">
          <Link to="/users" className="text-xs text-primary-600 hover:text-primary-700 font-medium">
            Manage Team &rarr;
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Room Status Board ────────────────────────────────────────────────────────

interface RoomStatusBoardProps {
  units: EnrichedUnit[];
  propertyName: string;
  selectedUnitIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onUnitClick: (unit: EnrichedUnit) => void;
  onQuickClean: (unit: EnrichedUnit) => void;
}

function RoomStatusBoard({
  units,
  propertyName,
  selectedUnitIds,
  onToggleSelect,
  onUnitClick,
  onQuickClean,
}: RoomStatusBoardProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const statusFilterId = useId();
  const searchId = useId();

  const filteredUnits = useMemo(() => {
    let result = units;
    if (statusFilter !== 'all') {
      result = result.filter((u) => u.housekeepingStatus === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((u) => u.name.toLowerCase().includes(q));
    }
    return result;
  }, [units, statusFilter, searchQuery]);

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label
              htmlFor={statusFilterId}
              className="block text-xs font-medium text-gray-600 mb-1"
            >
              Status
            </label>
            <div className="relative">
              <select
                id={statusFilterId}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilterValue)}
                className="appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {STATUS_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                aria-hidden="true"
              />
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label htmlFor={searchId} className="block text-xs font-medium text-gray-600 mb-1">
              Search
            </label>
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                aria-hidden="true"
              />
              <input
                id={searchId}
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by unit name..."
                className="w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>
      </div>

      <p className="sr-only" aria-live="polite">
        {filteredUnits.length} unit{filteredUnits.length !== 1 ? 's' : ''} shown
      </p>

      {/* Unit cards */}
      {filteredUnits.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
          <p className="text-sm text-gray-400">
            {units.length === 0
              ? 'No units configured for this property.'
              : 'No units match the current filters.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
            <Home size={16} className="text-gray-400" aria-hidden="true" />
            <h3 className="text-sm font-semibold text-gray-900">{propertyName}</h3>
            <span className="text-xs text-gray-400">({filteredUnits.length} units)</span>
          </div>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredUnits.map((unit) => (
              <UnitCard
                key={unit.id}
                unit={unit}
                isSelected={selectedUnitIds.has(unit.id)}
                onToggleSelect={onToggleSelect}
                onClick={() => onUnitClick(unit)}
                onQuickClean={() => onQuickClean(unit)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function HousekeepingPage() {
  const { user } = useAuth();
  const hasAccess = user?.role && ALLOWED_ROLES.includes(user.role as UserRole);

  const propertyId = useSelectedPropertyId();
  const { selectedProperty } = usePropertyContext();
  const { data: unitsData, isLoading: unitsLoading } = useUnits(propertyId || undefined);
  const { data: tasksData } = useTasks({ taskType: 'housekeeping' });
  const { data: staffData } = useHousekeepingStaff(propertyId || undefined);

  const [selectedUnitIds, setSelectedUnitIds] = useState<Set<string>>(new Set());
  const [modalUnit, setModalUnit] = useState<EnrichedUnit | null>(null);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [quickCleanUnitId, setQuickCleanUnitId] = useState<string | undefined>(undefined);

  const createTaskMutation = useCreateTask();

  const propertyName = selectedProperty?.name ?? 'Unknown Property';

  const allUnits: EnrichedUnit[] = useMemo(() => {
    if (!unitsData) return [];
    const rawUnits = Array.isArray(unitsData) ? unitsData : ((unitsData as Record<string, unknown>).data as Array<Record<string, unknown>>) ?? [];
    return rawUnits.map(
      (u: Record<string, unknown>): EnrichedUnit => ({
        id: u.id as string,
        name: (u.name as string) ?? '',
        type: (u.type as string) ?? '',
        housekeepingStatus:
          (u.housekeepingStatus as HousekeepingStatus) ?? HousekeepingStatus.CLEAN,
        propertyName,
        propertyId: propertyId,
        assignedTo: (u.assignedTo as string) ?? undefined,
      }),
    );
  }, [unitsData, propertyName, propertyId]);

  const tasks: Array<Record<string, unknown>> = useMemo(() => {
    if (!tasksData) return [];
    return Array.isArray(tasksData) ? tasksData : ((tasksData as Record<string, unknown>).data as Array<Record<string, unknown>>) ?? [];
  }, [tasksData]);

  const staff: StaffMember[] = useMemo(() => {
    if (!staffData) return [];
    return Array.isArray(staffData) ? staffData : [];
  }, [staffData]);

  // Enrich units with assignment data from tasks
  const enrichedUnits: EnrichedUnit[] = useMemo(() => {
    const assignmentMap = new Map<string, string>();
    for (const t of tasks) {
      if (t.assignedTo && t.unitId) {
        assignmentMap.set(t.unitId as string, t.assignedTo as string);
      }
    }
    return allUnits.map((u) => ({
      ...u,
      assignedTo: u.assignedTo ?? assignmentMap.get(u.id) ?? undefined,
    }));
  }, [allUnits, tasks]);

  const toggleSelectUnit = useCallback((id: string) => {
    setSelectedUnitIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedUnitIds(new Set());
  }, []);

  const handleQuickClean = useCallback(
    (unit: EnrichedUnit) => {
      const today = new Date().toISOString().split('T')[0];
      createTaskMutation.mutate({
        unitId: unit.id,
        taskType: 'housekeeping',
        title: `Clean — ${unit.name}`,
        priority: 'medium',
        dueAt: today,
        assignedTo: null,
      });
    },
    [createTaskMutation],
  );


  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary-50">
            <SprayCan size={20} className="text-primary-600" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Housekeeping</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Manage room status and cleaning assignments
            </p>
          </div>
        </div>
        {hasAccess && (
          <Button onClick={() => { setQuickCleanUnitId(undefined); setShowCreateTask(true); }}>
            <Plus size={16} aria-hidden="true" />
            Create Task
          </Button>
        )}
      </div>

      {!hasAccess ? (
        <AccessDeniedCard />
      ) : !propertyId ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
          <p className="text-sm text-gray-400">Please select a property to view housekeeping data.</p>
        </div>
      ) : unitsLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-primary-600" aria-hidden="true" />
          <span className="ml-2 text-sm text-gray-500">Loading housekeeping data...</span>
        </div>
      ) : (
        <>
          {/* Summary Stats */}
          <SummaryStats units={enrichedUnits} />

          {/* Two-column layout */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left: Room Status Board (2/3) */}
            <div className="flex-1 lg:w-2/3 min-w-0">
              <RoomStatusBoard
                units={enrichedUnits}
                propertyName={propertyName}
                selectedUnitIds={selectedUnitIds}
                onToggleSelect={toggleSelectUnit}
                onUnitClick={setModalUnit}
                onQuickClean={handleQuickClean}
              />
            </div>

            {/* Right: Assignment Panel (1/3) */}
            <div className="lg:w-1/3 shrink-0">
              <AssignmentPanel
                selectedUnitIds={selectedUnitIds}
                tasks={tasks}
                propertyId={propertyId}
                onAssignComplete={clearSelection}
              />
            </div>
          </div>

          {/* Status Change Modal */}
          <StatusChangeModal unit={modalUnit} onClose={() => setModalUnit(null)} />

          {/* Create Task SlideOver */}
          <SlideOver
            isOpen={showCreateTask}
            onClose={() => { setShowCreateTask(false); setQuickCleanUnitId(undefined); }}
            title="Create Task"
          >
            <CreateTaskForm
              units={enrichedUnits}
              staff={staff}
              propertyId={propertyId}
              propertyName={propertyName}
              defaultUnitId={quickCleanUnitId}
              onClose={() => { setShowCreateTask(false); setQuickCleanUnitId(undefined); }}
            />
          </SlideOver>
        </>
      )}
    </div>
  );
}
